import { writable } from 'svelte/store';

// Store for current active page
export const currentPage = writable<string>('dashboard');

// Store for current admin tab
export const currentAdminTab = writable<string>('users');

// Store for loading state
export const loading = writable<boolean>(false);

// Store for global error message
export const errorMessage = writable<string>('');

// Store for notifications
interface Notification {
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
    duration?: number; // in milliseconds
}

export const notifications = writable<Notification[]>([]);

/**
 * Add a notification
 */
export function addNotification(
    type: 'success' | 'error' | 'info' | 'warning',
    message: string,
    duration: number = 5000
): string {
    const id = generateId();
    
    notifications.update(items => [
        ...items,
        { id, type, message, duration }
    ]);
    
    // Auto-remove after duration
    if (duration > 0) {
        setTimeout(() => {
            removeNotification(id);
        }, duration);
    }
    
    return id;
}

/**
 * Remove a notification by ID
 */
export function removeNotification(id: string): void {
    notifications.update(items => items.filter(item => item.id !== id));
}

/**
 * Clear all notifications
 */
export function clearNotifications(): void {
    notifications.set([]);
}

/**
 * Helper function to generate random ID
 */
function generateId(): string {
    return Math.random().toString(36).substring(2, 9);
}

// Store for UI settings
interface UISettings {
    chartAnimations: boolean;
    dataTablePageSize: number;
    sidebarOpen: boolean;
}

const defaultSettings: UISettings = {
    chartAnimations: true,
    dataTablePageSize: 10,
    sidebarOpen: true
};

export const uiSettings = writable<UISettings>(defaultSettings);

/**
 * Initialize UI settings from localStorage
 */
export function initUISettings(): void {
    try {
        const savedSettings = localStorage.getItem('uiSettings');
        
        if (savedSettings) {
            const parsed = JSON.parse(savedSettings);
            uiSettings.set({
                ...defaultSettings,
                ...parsed
            });
        }
    } catch (error) {
        console.error('Failed to load UI settings:', error);
    }
}

/**
 * Save UI settings to localStorage
 */
export function saveUISettings(settings: UISettings): void {
    try {
        localStorage.setItem('uiSettings', JSON.stringify(settings));
    } catch (error) {
        console.error('Failed to save UI settings:', error);
    }
}

/**
 * Reset UI settings to defaults
 */
export function resetUISettings(): void {
    uiSettings.set(defaultSettings);
    saveUISettings(defaultSettings);
}
